import { useMemo } from "react";
import { Link } from "react-router-dom";
import { useBookings } from "../hooks/useBookings";
import { CancelBookingButton } from "../components/CancelBookingButton";
import { formatFullDate, parseBookingSlotId } from "../utils/dates";
import type { Booking } from "../types/gym.types";

function groupByDate(bookings: Booking[]): [string, Booking[]][] {
  const byDate = new Map<string, Booking[]>();
  for (const b of bookings) {
    const { date } = parseBookingSlotId(b.slotId);
    const list = byDate.get(date) ?? [];
    list.push(b);
    byDate.set(date, list);
  }
  for (const list of byDate.values()) list.sort((a, b) => a.slotId.localeCompare(b.slotId));
  return [...byDate.entries()].sort(([a], [b]) => a.localeCompare(b));
}

export function MyBookingsPage() {
  const { bookings, isLoading, error, refresh } = useBookings();
  const grouped = useMemo(() => groupByDate(bookings), [bookings]);

  function handleCancelled() {
    void refresh();
  }

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <h1>My Bookings</h1>
          <p className="page-sub">Your upcoming gym sessions.</p>
        </div>
      </header>

      {error ? (
        <div className="state-panel">
          <h2>Couldn&rsquo;t load your bookings</h2>
          <p>{error}</p>
        </div>
      ) : isLoading ? (
        <div className="state-panel">Loading your bookings…</div>
      ) : grouped.length === 0 ? (
        <div className="state-panel">
          <p>No upcoming bookings yet.</p>
          <Link to="/gym" className="btn btn-primary">
            Go to Schedule
          </Link>
        </div>
      ) : (
        <div className="bookings-list">
          {grouped.map(([date, dayBookings]) => (
            <section className="bookings-day" key={date}>
              <h2 className="bookings-day-heading">{formatFullDate(date)}</h2>
              <ul className="bookings-day-items">
                {dayBookings.map((booking) => {
                  const { startTime } = parseBookingSlotId(booking.slotId);
                  return (
                    <li className="booking-row" key={booking.id}>
                      <span className="booking-time">{startTime}</span>
                      <span className="booking-type">
                        {booking.type === "private" ? "Private session" : "Gym slot"}
                      </span>
                      <CancelBookingButton booking={booking} onCancelled={handleCancelled} />
                    </li>
                  );
                })}
              </ul>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}
